import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BarChart3, BookOpenCheck, CalendarDays, ChevronRight, Shuffle, Sparkles } from 'lucide-react';

type LanguageLevel = 1 | 2;

const languageThemes = {
  2: {
    name: '中文理科（IPA）',
    heading: '真题与资料',
    headingDescription: '按原卷练习、查看已录入题型；尚未开放的功能可查看预告',
    paperTitle: '试卷与复习资料',
    paperDescription: '按卷别选择已录入试卷与冲刺复习资料，固定顺序作答并保存记录。',
    topicTitle: '题型与题量',
    topicDescription: '查看已录入资料的题型分布，并按分类练习。',
    hero: 'from-[#065F46] to-[#0D9488]',
    heroShadow: 'shadow-emerald-500/20',
    heroEyebrow: 'text-emerald-100',
    section: 'border-emerald-100 bg-emerald-50/50',
    moocSection: 'border-teal-100 bg-teal-50/50',
    icon: 'bg-emerald-100 text-emerald-700',
    moocIcon: 'bg-teal-100 text-teal-700',
    card: 'border-emerald-100 hover:border-emerald-200',
    moocCard: 'border-teal-100 hover:border-teal-200',
    text: 'text-emerald-950',
    link: 'text-emerald-700 hover:text-emerald-900',
  },
  1: {
    name: '中文文科（IPS）',
    heading: '真题',
    headingDescription: '已录入往年试卷、历史题型统计；尚未开放的功能可查看预告',
    paperTitle: '往年试卷',
    paperDescription: '按考试月份选择已录入的往年试卷，固定顺序作答并保存记录。',
    topicTitle: '题型与概率',
    topicDescription: '查看已录入往年卷的题型比例，并按分类练习。',
    hero: 'from-[#9A3412] to-[#D97706]',
    heroShadow: 'shadow-orange-500/20',
    heroEyebrow: 'text-orange-100',
    section: 'border-orange-100 bg-orange-50/50',
    moocSection: 'border-amber-100 bg-amber-50/50',
    icon: 'bg-orange-100 text-orange-700',
    moocIcon: 'bg-amber-100 text-amber-800',
    card: 'border-orange-100 hover:border-orange-200',
    moocCard: 'border-amber-100 hover:border-amber-200',
    text: 'text-orange-950',
    link: 'text-orange-700 hover:text-orange-900',
  },
} as const;

const LanguageHome: React.FC<{ level: LanguageLevel }> = ({ level }) => {
  const theme = languageThemes[level];
  const basePath = `/csca/simulation/${level}`;
  const cardClass = 'group rounded-3xl border bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg';
  const status = (available: boolean) => (
    <span className={`rounded-full px-3 py-1 text-xs font-bold ${available ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
      {available ? '已开放' : 'Upcoming'}
    </span>
  );
  const cards = [
    { title: theme.paperTitle, description: theme.paperDescription, action: '选择试卷', to: `${basePath}/papers`, icon: CalendarDays, available: true },
    { title: theme.topicTitle, description: theme.topicDescription, action: '查看统计', to: `${basePath}/topics`, icon: BarChart3, available: true },
    { title: '随机组卷', description: '按本科目的真题结构抽取题目，组成新的练习卷。', action: '查看预告', to: `${basePath}/upcoming/random`, icon: Shuffle, available: false },
  ];
  const moocCards = [
    { title: '按分类做题', description: '从本科目的 MOOC 题库按分类选择变式练习。', action: '查看预告', to: `${basePath}/upcoming/mooc-topics`, icon: BarChart3 },
    { title: '随机 MOOC', description: '从本科目的 MOOC 题库随机抽取练习题。', action: '查看预告', to: `${basePath}/upcoming/mooc-random`, icon: Shuffle },
  ];
  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <Link to="/" className={`inline-flex items-center gap-2 text-sm font-bold ${theme.link}`}><ArrowLeft className="h-4 w-4" /> 返回考试首页</Link>
      <section className={`rounded-3xl bg-gradient-to-br ${theme.hero} p-6 text-white shadow-xl ${theme.heroShadow} md:p-10`}>
        <div className={`text-sm font-bold uppercase tracking-[0.2em] ${theme.heroEyebrow}`}>Huanle Mandarin Exam</div>
        <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">CSCA {theme.name}</h1>
      </section>
      <section className={`space-y-4 rounded-3xl border p-4 md:p-6 ${theme.section}`}>
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${theme.icon}`}><BookOpenCheck className="h-6 w-6" /></div>
          <div><h2 className={`text-xl font-black ${theme.text}`}>{theme.heading}</h2><p className="text-sm text-slate-500">{theme.headingDescription}</p></div>
        </div>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {cards.map(card => {
            const Icon = card.icon;
            return <Link key={card.title} to={card.to} className={`${cardClass} ${theme.card}`}>
              <div className="flex items-start justify-between"><div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${theme.icon}`}><Icon className="h-7 w-7" /></div>{status(card.available)}</div>
              <h3 className={`mt-5 text-2xl font-bold ${theme.text}`}>{card.title}</h3>
              <p className="mt-2 text-sm leading-6 text-gray-500">{card.description}</p>
              <div className={`mt-6 inline-flex items-center gap-1 font-bold ${theme.link}`}>{card.action} <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></div>
            </Link>;
          })}
        </div>
      </section>
      <section className={`space-y-4 rounded-3xl border p-4 md:p-6 ${theme.moocSection}`}>
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${theme.moocIcon}`}><Sparkles className="h-6 w-6" /></div>
          <div><h2 className={`text-xl font-black ${theme.text}`}>MOOC</h2><p className="text-sm text-slate-500">本科目的变式练习即将开放</p></div>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          {moocCards.map(card => {
            const Icon = card.icon;
            return <Link key={card.title} to={card.to} className={`${cardClass} ${theme.moocCard}`}>
              <div className="flex items-start justify-between"><div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${theme.moocIcon}`}><Icon className="h-7 w-7" /></div>{status(false)}</div>
              <h3 className={`mt-5 text-2xl font-bold ${theme.text}`}>{card.title}</h3>
              <p className="mt-2 text-sm leading-6 text-gray-500">{card.description}</p>
              <div className={`mt-6 inline-flex items-center gap-1 font-bold ${theme.link}`}>{card.action} <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></div>
            </Link>;
          })}
        </div>
      </section>
    </div>
  );
};

const MathHome: React.FC = () => (
  <div className="mx-auto max-w-5xl space-y-8">
    <div>
      <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-[#4F46E5] hover:text-indigo-700">
        <ArrowLeft className="h-4 w-4" /> 返回考试首页
      </Link>
    </div>

    <section className="rounded-3xl bg-gradient-to-br from-[#312E81] to-[#4F46E5] p-6 text-white shadow-xl shadow-indigo-500/20 md:p-10">
      <div className="text-sm font-bold uppercase tracking-[0.2em] text-indigo-200">Huanle Mandarin Exam</div>
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">CSCA 考试</h1>
    </section>

    <section className="space-y-4 rounded-3xl border border-indigo-100 bg-indigo-50/50 p-4 md:p-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-100 text-[#4F46E5]"><BookOpenCheck className="h-6 w-6" /></div>
        <div><h2 className="text-xl font-black text-[#312E81]">真题</h2><p className="text-sm text-slate-500">完整往年试卷、历史题型统计与按真题结构随机组卷</p></div>
      </div>
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
      <Link
        to="/csca/simulation/3/papers"
        className="group rounded-3xl border border-indigo-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-indigo-200 hover:shadow-lg"
      >
        <div className="flex items-start justify-between">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-100 text-[#4F46E5]">
            <CalendarDays className="h-7 w-7" />
          </div>
          <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-700">已开放</span>
        </div>
        <h2 className="mt-5 text-2xl font-bold text-[#312E81]">往年试卷</h2>
        <p className="mt-2 text-sm leading-6 text-gray-500">按年份和月份选择完整试卷，固定顺序作答并保存考试记录。</p>
        <div className="mt-6 inline-flex items-center gap-1 font-bold text-[#4F46E5]">
          选择试卷 <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
        </div>
      </Link>

      <Link
        to="/csca/simulation/3/topics"
        className="group rounded-3xl border border-violet-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-violet-200 hover:shadow-lg"
      >
        <div className="flex items-start justify-between">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-100 text-violet-600">
            <BarChart3 className="h-7 w-7" />
          </div>
          <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-700">已开放</span>
        </div>
        <h2 className="mt-5 text-2xl font-bold text-[#312E81]">题型与概率</h2>
        <p className="mt-2 text-sm leading-6 text-gray-500">按粗类型和细分题型查看五套往年卷的出题比例与每套题量。</p>
        <div className="mt-6 inline-flex items-center gap-1 font-bold text-violet-600">
          查看统计 <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
        </div>
      </Link>

      <Link to="/csca/random" className="group rounded-3xl border border-fuchsia-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-fuchsia-200 hover:shadow-lg">
        <div className="flex items-start justify-between">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-fuchsia-100 text-fuchsia-600">
            <Shuffle className="h-7 w-7" />
          </div>
          <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-700">已开放</span>
        </div>
        <h2 className="mt-5 text-2xl font-bold text-[#312E81]">随机组卷</h2>
        <p className="mt-2 text-sm leading-6 text-gray-500">按往年平均粗分、细分和原题位置，从真实题库无重复抽取01–48共48题。</p>
        <div className="mt-6 inline-flex items-center gap-1 font-bold text-fuchsia-600">查看组卷结构 <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></div>
      </Link>
      </div>
    </section>

    <section className="space-y-4 rounded-3xl border border-cyan-100 bg-cyan-50/50 p-4 md:p-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-100 text-cyan-700"><Sparkles className="h-6 w-6" /></div>
        <div><h2 className="text-xl font-black text-[#312E81]">MOOC</h2><p className="text-sm text-slate-500">5 套数学模拟题与 AI 变式题，独立于往年真题题库</p></div>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        <Link to="/csca/mooc/topics" className="group rounded-3xl border border-cyan-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-cyan-200 hover:shadow-lg">
          <div className="flex items-start justify-between"><div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-100 text-cyan-700"><BarChart3 className="h-7 w-7" /></div><span className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-700">已开放</span></div>
          <h2 className="mt-5 text-2xl font-bold text-[#312E81]">按分类做题</h2>
          <p className="mt-2 text-sm leading-6 text-gray-500">先选粗分类或细分类，再自行设定本次从 MOOC 题库抽取的题量。</p>
          <div className="mt-6 inline-flex items-center gap-1 font-bold text-cyan-700">选择分类 <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></div>
        </Link>
        <Link to="/csca/mooc/random" className="group rounded-3xl border border-teal-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-teal-200 hover:shadow-lg">
          <div className="flex items-start justify-between"><div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-100 text-teal-700"><Shuffle className="h-7 w-7" /></div><span className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-700">已开放</span></div>
          <h2 className="mt-5 text-2xl font-bold text-[#312E81]">随机 MOOC</h2>
          <p className="mt-2 text-sm leading-6 text-gray-500">固定 48 题，优先抽取未做过的模拟题，再抽取 AI 题；保留完整答题、计时和成绩流程。</p>
          <div className="mt-6 inline-flex items-center gap-1 font-bold text-teal-700">设置题量 <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></div>
        </Link>
      </div>
    </section>
  </div>
);

const CSCAHome: React.FC<{ level?: 1 | 2 | 3 }> = ({ level = 3 }) => level === 3 ? <MathHome /> : <LanguageHome level={level} />;

export default CSCAHome;
